import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { THEME_COLORS, DARK_THEME_COLORS, SHADOWS, DARK_SHADOWS } from '../theme/themeConfig';

type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  toggleTheme: () => void;
  isDark: boolean;
  colors: typeof THEME_COLORS;
  shadows: typeof SHADOWS;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useThemeMode = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
  return ctx;
};

interface ThemeModeProviderProps {
  children: React.ReactNode;
}

export const ThemeModeProvider: React.FC<ThemeModeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    try {
      const saved = localStorage.getItem('themeMode');
      if (saved === 'dark' || saved === 'light') return saved;
      // Respect OS preference on first load
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    } catch {
      // ignore
    }
    return 'light';
  });

  useEffect(() => {
    try {
      localStorage.setItem('themeMode', mode);
    } catch {
      // ignore
    }
  }, [mode]);

  const toggleTheme = () => setMode(prev => (prev === 'light' ? 'dark' : 'light'));

  const isDark = mode === 'dark';
  const colors = isDark ? DARK_THEME_COLORS : THEME_COLORS;
  const shadows = isDark ? DARK_SHADOWS : SHADOWS;

  const value = useMemo(
    () => ({ mode, toggleTheme, isDark, colors, shadows }),
    [mode, isDark]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeModeProvider;